<template>
  <div id="a-pricing-strategy-planning">
    <a-base-component :subscribed_modules="subscribed_modules">
      <template v-slot:default>
        <div class="d-flex justify-space-between align-center mt-6 mb-8">
          <div class="font-weight-bold">
            {{ $t("pricing_strategy_planning_description") }}
          </div>
          <div class="d-flex align-center" style="gap: 10px">
            <v-select
              v-model="planning_rate_plan_id"
              :label="$t('_rate')"
              :items="strategy_rates"
              item-text="code"
              item-value="id"
              :rules="[v => !!v || $t('required')]"
              outlined
              rounded
              dense
              hide-details
              disabled
            ></v-select>
            <v-btn color="secondary" rounded @click="openApplyPeriodDialog()">
              {{ $t("Apply_strategies") }}
            </v-btn>
            <v-btn color="secondary" rounded @click="openSettingsDialog()">
              <v-icon>mdi-cog-outline</v-icon>
            </v-btn>
          </div>
        </div>
        <div class="calendar-container mb-4">
          <v-row v-for="month in Object.keys(grouped_planning)" :key="month">
            <!-- Nombre del mes a la izquierda -->
            <v-col
              cols="12"
              md="1"
              class="text-h6 text-uppercase text-center font-weight-bold pt-8 pb-0"
            >
              <div
                class="d-flex flex-column justify-center"
                style="line-height: 1"
              >
                <span>
                  {{ getMonthName(month) }}
                </span>
                <span class="text-body-1">
                  {{ getYear(month) }}
                </span>
              </div>
            </v-col>

            <!-- Semanas a la derecha -->
            <v-col
              cols="12"
              md="11"
              class="py-0 pb-1 d-flex flex-column"
              style="gap: 4px"
            >
              <v-card
                v-for="week in Object.keys(grouped_planning[month])"
                :key="week"
                class="week-row"
                elevation="0"
                rounded="xl"
                :style="`grid-template-columns: repeat(${max_rows}, 1fr)`"
              >
                <!-- Días de la semana -->
                <div
                  v-for="day in grouped_planning[month][week]"
                  :key="day.date"
                  class="d-flex flex-column align-center pa-3 pb-4 day-cell"
                  :class="{
                    'empty-day': day.isEmpty,
                    'clickable-day': !day.isEmpty
                  }"
                  style="gap: 10px"
                  @click="day.isEmpty ? null : openApplyStrategyDialog(day)"
                >
                  <!-- Header del día -->
                  <div
                    v-if="!day.isEmpty && day.rate"
                    class="d-flex align-center text-caption grey--text text-uppercase"
                    style="gap: 5px"
                  >
                    <span>
                      {{ getDayNumber(day.date) }}
                    </span>
                    <span>
                      {{ getDayName(day.date) }}
                    </span>
                  </div>

                  <!-- Precio -->
                  <a-kpi
                    v-if="!day.isEmpty && day.rate"
                    kpi="price"
                    :value="day.rate"
                    :vs_values="[day.rate_vs]"
                    :vs_periods="[day.spot_vs]"
                    class="font-weight-regular"
                    small
                    absolute
                  ></a-kpi>

                  <!-- Estrategias -->
                  <div
                    v-if="
                      !day.isEmpty &&
                        day.strategies &&
                        day.strategies.length > 0
                    "
                    class="d-flex flex-column strategies-container"
                    style="gap: 5px"
                  >
                    <div
                      v-for="(strategy, index) in day.strategies"
                      :key="strategy.id || index"
                      class="pa-2 text-caption font-weight-bold strategy-chip"
                      :style="
                        `background-color: ${
                          strategy.color
                        }; color: ${intensifyColor(strategy.color)}`
                      "
                      @click.stop.prevent="routeToStrategyDetail(strategy.id)"
                    >
                      {{ strategy.name }}
                    </div>
                  </div>
                  <div
                    v-else-if="!day.isEmpty"
                    class="text-caption grey--text text-center"
                  >
                    {{ $t("without_strategies") }}
                  </div>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </div>

        <!-- Modal de configuración -->
        <v-dialog
          v-model="settings_dialog"
          :fullscreen="$vuetify.breakpoint.xsOnly"
          max-width="400"
        >
          <v-card>
            <v-card-title center class="headline justify-center">
              {{ $t("Visualization_settings") }}
            </v-card-title>
            <v-card-text>
              <v-form ref="settings_form" v-model="settings_form">
                <v-row>
                  <v-col cols="12">
                    {{ $t("pricing_strategy_planning_visualization_settings") }}
                  </v-col>
                  <v-col cols="12">
                    <v-select
                      v-model="room_type_id"
                      :label="$t('Categories')"
                      :items="strategy_room_types"
                      :item-text="item => item.name"
                      item-value="id"
                      :rules="[v => !!v || $t('required')]"
                      outlined
                      rounded
                      dense
                    ></v-select>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
            <v-card-actions class="pt-0 pb-5 justify-center">
              <v-btn
                :disabled="!settings_form"
                color="secondary"
                rounded
                class="pl-5 pr-5"
                @click="updateSettings()"
              >
                {{ $t("Apply") }}
              </v-btn>
              <v-btn
                text
                rounded
                depressed
                class="pl-5 pr-5"
                @click="settings_dialog = false"
              >
                {{ $t("cancel") }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Modal de estrategia para un día específico -->
        <v-dialog
          v-model="apply_strategy_dialog"
          :fullscreen="$vuetify.breakpoint.xsOnly"
          max-width="500"
        >
          <v-card>
            <v-card-title class="headline justify-space-between">
              {{ $t("Apply_strategy_for") }}
              {{ getFormattedDate(selected_date) }}
            </v-card-title>
            <v-card-text>
              <div class="text-subtitle-2 mt-3 mb-5">
                {{
                  $t("Select_the_strategy_you_want_to_apply_for", {
                    date: getFullFormattedDate(selected_date)
                  })
                }}
              </div>
              <v-form ref="apply_strategy_form" v-model="apply_strategy_form">
                <v-row>
                  <v-col cols="12">
                    <v-select
                      v-model="selected_strategy_id"
                      :label="$t('Strategy')"
                      :items="itemsStrategies"
                      :item-text="item => item.name"
                      item-value="id"
                      outlined
                      rounded
                      dense
                    >
                      <template v-slot:selection="{ item, index }">
                        <v-row>
                          <v-col
                            cols="12"
                            md="8"
                            class="d-flex align-center pa-1"
                          >
                            <v-avatar size="20" :color="item.color"></v-avatar>
                            <span class="ml-2">{{ item.name }}</span>
                          </v-col>
                          <v-col
                            cols="12"
                            md="4"
                            class="d-flex align-center pa-1"
                          >
                            <div class="text-uppercase font-weight-bold">
                              {{ getStrategyRatesCode(item.rate_plan_id) }}
                            </div>
                          </v-col>
                        </v-row>
                      </template>
                    </v-select>
                  </v-col>
                </v-row>
              </v-form>
            </v-card-text>
            <v-card-actions class="pt-0 pb-5 justify-center">
              <v-btn
                :disabled="!apply_strategy_form"
                color="secondary"
                rounded
                class="pl-5 pr-5"
                @click="updateDayStrategy()"
              >
                {{ $t("Apply") }}
              </v-btn>
              <v-btn
                text
                rounded
                depressed
                class="pl-5 pr-5"
                @click="apply_strategy_dialog = false"
              >
                {{ $t("cancel") }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <!-- Modal para aplicar estrategia en un periodo -->
        <v-dialog
          v-model="apply_period_dialog"
          :fullscreen="$vuetify.breakpoint.xsOnly"
          max-width="500"
        >
          <v-card>
            <v-card-title class="headline justify-space-between">
              {{ $t("Apply_strategy") }}
            </v-card-title>
            <v-card-text>
              <div class="text-subtitle-2 mb-4">
                {{ $t("apply_strategy_description") }}
              </div>

              <v-form ref="apply_period_form" v-model="apply_period_form">
                <!-- Selección de periodo -->
                <div class="mb-4">
                  <div class="text-caption grey--text mb-2">
                    {{ $t("Select_period_to_apply") }}
                  </div>
                  <v-row>
                    <v-col cols="12">
                      <v-menu :close-on-content-click="false" min-width="auto">
                        <template v-slot:activator="{ on: onDate }">
                          <v-text-field
                            v-on="{ ...onDate }"
                            :value="getFormattedPeriod(period_dates)"
                            readonly
                            outlined
                            rounded
                            dense
                            hide-details
                            append-icon="mdi-calendar"
                          >
                          </v-text-field>
                        </template>
                        <v-date-picker
                          v-model="period_dates"
                          first-day-of-week="1"
                          scrollable
                          range
                          :min="today"
                        ></v-date-picker>
                      </v-menu>
                    </v-col>
                    <v-col cols="12">
                      <!-- Días de la semana incluidos -->
                      <div>
                        <div class="text-caption grey--text mb-3">
                          {{ $t("Weekdays_included_in_selection") }}
                        </div>
                        <div class="d-flex align-center" style="gap: 10px">
                          <div v-for="day in weekdays" :key="day.value">
                            <span>{{ day.name }}</span>
                            <v-switch
                              v-model="selected_weekdays[day.value]"
                              color="secondary"
                              dense
                              hide-details
                              class="mt-0"
                            ></v-switch>
                          </div>
                        </div>
                      </div>
                    </v-col>
                    <v-col cols="12" class="mt-4 pb-0">
                      <v-select
                        v-model="period_strategy_id"
                        :label="$t('Strategy')"
                        :items="itemsStrategies"
                        :item-text="item => item.name"
                        item-value="id"
                        outlined
                        rounded
                        dense
                      >
                        <template v-slot:selection="{ item, index }">
                          <v-row>
                            <v-col
                              cols="12"
                              md="8"
                              class="d-flex align-center pa-1"
                            >
                              <v-avatar
                                size="20"
                                :color="item.color"
                              ></v-avatar>
                              <span class="ml-2">{{ item.name }}</span>
                            </v-col>
                            <v-col
                              cols="12"
                              md="4"
                              class="d-flex align-center pa-1"
                            >
                              <div class="text-uppercase font-weight-bold">
                                {{ getStrategyRatesCode(item.rate_plan_id) }}
                              </div>
                            </v-col>
                          </v-row>
                        </template>
                      </v-select>
                    </v-col>
                  </v-row>
                </div>
              </v-form>
            </v-card-text>
            <v-card-actions class="pt-0 pb-5 justify-center">
              <v-btn
                :disabled="!apply_period_form || !isValidPeriod()"
                color="secondary"
                rounded
                class="pl-5 pr-5"
                @click="updatePeriodStrategy()"
              >
                {{ $t("Apply") }}
              </v-btn>
              <v-btn
                text
                rounded
                depressed
                class="pl-5 pr-5"
                @click="apply_period_dialog = false"
              >
                {{ $t("cancel") }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </template>
      <template v-slot:loading>
        <v-skeleton-loader type="table"> </v-skeleton-loader>
      </template>
    </a-base-component>
  </div>
</template>

<script>
import aBaseComponent from "@/components/aBaseComponent";
import aKpi from "@/components/rms_ui/aKpi.vue";
import * as Utils from "@/utils.js";
import Vue from "vue";
import { mapGetters, mapState } from "vuex";

export default {
  name: "a-pricing-strategy-planning",
  components: {
    aBaseComponent,
    aKpi
  },
  data: () => ({
    subscribed_modules: [
      "pricingstrategy_planning",
      "pricingstrategy_strategy_rates",
      "pricingstrategy_strategy_room_types",
      "pricingstrategy_strategies"
    ],
    settings_dialog: false,
    settings_form: false,
    room_type_id: 0,
    apply_strategy_dialog: false,
    apply_strategy_form: false,
    selected_date: null,
    selected_strategy_id: null,
    apply_period_dialog: false,
    apply_period_form: false,
    period_dates: [],
    period_strategy_id: null,
    selected_weekdays: {
      1: true,
      2: true,
      3: true,
      4: true,
      5: true,
      6: true,
      0: true
    }
  }),
  computed: {
    planning_from_date: {
      get() {
        return this.$store.state.pricingstrategy.planning_from_date;
      },
      set(value) {
        this.$store.dispatch("setPricingStrategyPlanningFromDate", value);
      }
    },
    planning_to_date: {
      get() {
        return this.$store.state.pricingstrategy.planning_to_date;
      },
      set(value) {
        this.$store.dispatch("setPricingStrategyPlanningToDate", value);
      }
    },
    planning_room_type_id: {
      get() {
        return this.$store.state.pricingstrategy.planning_room_type_id;
      },
      set(value) {
        this.$store.dispatch("setPricingStrategyPlanningRoomTypeId", value);
      }
    },
    planning_rate_plan_id: {
      get() {
        return this.$store.state.pricingstrategy.planning_rate_plan_id;
      },
      set(value) {
        this.$store.dispatch("setPricingStrategyPlanningRatePlanId", value);
      }
    },
    max_rows() {
      let rows = 7;
      if (this.$vuetify.breakpoint.mdAndDown) {
        rows = 5;
      }
      return rows;
    },
    today() {
      return Vue.moment().format("YYYY-MM-DD");
    },
    weekdays() {
      return [
        { name: this.$t("Monday"), value: 1 },
        { name: this.$t("Tuesday"), value: 2 },
        { name: this.$t("Wednesday"), value: 3 },
        { name: this.$t("Thursday"), value: 4 },
        { name: this.$t("Friday"), value: 5 },
        { name: this.$t("Saturday"), value: 6 },
        { name: this.$t("Sunday"), value: 0 }
      ];
    },
    itemsStrategies() {
      // Añadimos un item para que se pueda seleccionar "No aplicar estrategia"
      return [
        {
          id: null,
          name: this.$t("no_apply_strategy"),
          color: "grey"
        },
        ...this.strategies
      ];
    },
    grouped_planning() {
      // Agrupamos por meses y semanas completas (sin días vacíos)
      if (!this.planning) {
        return {};
      }

      const grouped = {};
      const dates = Object.keys(this.planning).sort();

      // Crear array de todos los días con sus datos
      let allDays = dates.map(date => ({
        date: date,
        ...this.planning[date]
      }));

      // Solo agregar días vacíos al principio si hay datos
      if (allDays.length > 0) {
        const firstDate = Vue.moment(allDays[0].date);
        const firstDateDayOfWeek = firstDate.isoWeekday(); // 1=Lunes, 7=Domingo

        // Si la primera fecha no es lunes, agregar días vacíos desde el lunes de esa semana
        if (firstDateDayOfWeek !== 1) {
          const daysToAdd = [];
          const mondayOfFirstWeek = firstDate.clone().startOf("isoWeek"); // Lunes de la semana de la primera fecha

          // Agregar días vacíos desde el lunes hasta el día anterior a la primera fecha
          let currentDay = mondayOfFirstWeek.clone();
          while (currentDay.isBefore(firstDate, "day")) {
            daysToAdd.push({
              date: currentDay.format("YYYY-MM-DD"),
              isEmpty: true // Marcador para días vacíos
            });
            currentDay.add(1, "day");
          }

          // Agregar los días vacíos al principio
          allDays = [...daysToAdd, ...allDays];
        }
      }

      // Dividir en semanas completas de exactamente max_rows días
      for (let i = 0; i < allDays.length; i += this.max_rows) {
        const week = allDays.slice(i, i + this.max_rows);

        // Solo procesar si la semana tiene días
        if (week.length > 0) {
          // Determinar el mes de la semana basándose en el primer día no vacío
          const firstNonEmptyDay = week.find(day => !day.isEmpty);
          const monthKey = firstNonEmptyDay
            ? Vue.moment(firstNonEmptyDay.date).format("YYYY-MM")
            : Vue.moment(week[0].date).format("YYYY-MM");

          if (!grouped[monthKey]) {
            grouped[monthKey] = {};
          }

          // Determinar el número de semana para este mes
          const existingWeeks = Object.keys(grouped[monthKey]).length;
          grouped[monthKey][`week-${existingWeeks + 1}`] = week;
        }
      }

      return grouped;
    },
    ...mapState({
      planning: state => state.pricingstrategy.planning,
      strategy_rates: state => state.pricingstrategy.strategy_rates,
      strategy_room_types: state => state.pricingstrategy.strategy_room_types,
      strategies: state => state.pricingstrategy.strategies
    }),
    ...mapGetters({
      current_hotel: "current_hotel",
      current_user: "current_user"
    })
  },
  methods: {
    intensifyColor(hex) {
      return Utils.intensifyColor(hex);
    },
    getMonthName(month) {
      return Vue.moment(month, "YYYY-MM").format("MMMM");
    },
    getYear(month) {
      return Vue.moment(month, "YYYY-MM").format("YYYY");
    },
    getDayNumber(date) {
      return Vue.moment(date).format("D");
    },
    getDayName(date) {
      return Vue.moment(date).format("dddd");
    },
    getFullFormattedDate(date) {
      return Vue.moment(date).format("dddd DD MMMM YYYY");
    },
    getFormattedDate(date) {
      return Vue.moment(date).format("DD/MM");
    },
    getStrategyRatesCode(id) {
      return this.strategy_rates.find(rate => rate.id === id)
        ? this.strategy_rates.find(rate => rate.id === id).code
        : "";
    },
    // Settings form
    openSettingsDialog() {
      this.room_type_id = this.planning_room_type_id;
      this.settings_dialog = true;
    },
    updateSettings() {
      this.settings_dialog = false;
      this.planning_room_type_id = this.room_type_id;
    },
    // Apply strategy form
    openApplyStrategyDialog(day) {
      this.selected_date = day.date;
      this.apply_strategy_dialog = true;
      this.selected_strategy_id =
        day.strategies && day.strategies.length > 0
          ? day.strategies[0].id
          : null;
    },
    updateDayStrategy() {
      this.$store.dispatch("setSnackbar", {
        title: this.$t("updating_strategy"),
        color: "secondary"
      });
      this.$store
        .dispatch("setPricingStrategyPlanning", {
          hotel_id: this.current_hotel.id,
          date: this.selected_date,
          strategy_id: this.selected_strategy_id
        })
        .then(() => {
          this.apply_strategy_dialog = false;
          this.selected_strategy_id = null;
          this.$store.dispatch("setSnackbar", {
            title: this.$t("strategy_applied"),
            color: "accent"
          });
        })
        .catch(() => {
          this.apply_strategy_dialog = false;
          this.selected_strategy_id = null;
          Utils.showError(this, "strategy_not_applied");
        });
    },

    // Apply period strategy form -----------------------------------------------
    openApplyPeriodDialog() {
      // Inicializar con fechas por defecto (próximos 30 días)
      const fromDate = Vue.moment().format("YYYY-MM-DD");
      const toDate = Vue.moment()
        .add(30, "days")
        .format("YYYY-MM-DD");
      this.period_dates = [fromDate, toDate];
      this.period_strategy_id = null;
      this.apply_period_dialog = true;
    },
    getFormattedPeriod() {
      if (!this.period_dates || this.period_dates.length < 2) {
        return "";
      }
      const fromDate = Vue.moment(this.period_dates[0]).format("DD MMMM YYYY");
      const toDate = Vue.moment(this.period_dates[1]).format("DD MMMM YYYY");
      return `${this.$t("From")} ${fromDate} ${this.$t("to")} ${toDate}`;
    },
    isValidPeriod() {
      return (
        this.period_dates &&
        this.period_dates.length === 2 &&
        Object.values(this.selected_weekdays).some(day => day === true)
      );
    },
    updatePeriodStrategy() {
      this.$store.dispatch("setSnackbar", {
        title: this.$t("updating_strategy_period"),
        color: "secondary"
      });
      this.$store
        .dispatch("setPricingStrategyPlanningPeriod", {
          hotel_id: this.current_hotel.id,
          from_date: this.period_dates[0],
          to_date: this.period_dates[1],
          weekdays: Object.keys(this.selected_weekdays)
            .filter(key => this.selected_weekdays[key])
            .map(key => parseInt(key)),
          strategy_id: this.period_strategy_id
        })
        .then(() => {
          this.apply_period_dialog = false;
          this.period_strategy_id = null;
          this.$store.dispatch("setSnackbar", {
            title: this.$t("strategy_applied_to_period"),
            color: "accent"
          });
        })
        .catch(error => {
          this.apply_period_dialog = false;
          this.period_strategy_id = null;
          Utils.showError(this, "strategy_not_applied_to_period");
        });
    },

    // Route to strategy detail ------------------------------------------------
    routeToStrategyDetail(strategy_id) {
      this.$router.push({
        name: "pricing_strategy_strategy_detail",
        params: { strategy_id: strategy_id }
      });
    },

    // Initialize filters ------------------------------------------------
    initFilters() {
      if (
        this.current_hotel &&
        this.current_hotel.id &&
        this.current_user &&
        this.current_user.id &&
        this.current_user.settings
      ) {
        // Actualitzem la room type
        if (
          this.current_user.settings.pricingstrategy_planning_room_type &&
          this.current_user.settings.pricingstrategy_planning_room_type[
            this.current_hotel.id
          ] &&
          this.strategy_room_types &&
          this.strategy_room_types.length > 0 &&
          this.strategy_room_types.find(
            room_type =>
              room_type.id ===
              this.current_user.settings.pricingstrategy_planning_room_type[
                this.current_hotel.id
              ]
          )
        ) {
          this.planning_room_type_id = this.current_user.settings.pricingstrategy_planning_room_type[
            this.current_hotel.id
          ];
        } else {
          this.planning_room_type_id =
            this.strategy_room_types && this.strategy_room_types.length > 0
              ? this.strategy_room_types[0].id
              : null;
        }

        // Actualitzem la rate plan
        if (
          this.current_user.settings.pricingstrategy_planning_rate_plan &&
          this.current_user.settings.pricingstrategy_planning_rate_plan[
            this.current_hotel.id
          ] &&
          this.strategy_rates &&
          this.strategy_rates.length > 0 &&
          this.strategy_rates.find(
            rate =>
              rate.id ===
              this.current_user.settings.pricingstrategy_planning_rate_plan[
                this.current_hotel.id
              ]
          )
        ) {
          this.planning_rate_plan_id = this.current_user.settings.pricingstrategy_planning_rate_plan[
            this.current_hotel.id
          ];
        } else {
          this.planning_rate_plan_id =
            this.strategy_rates && this.strategy_rates.length > 0
              ? this.strategy_rates[0].id
              : null;
        }
      }
    }
  },
  watch: {
    strategy_rates() {
      this.initFilters();
    },
    strategy_room_types() {
      this.initFilters();
    },
    strategies() {
      this.initFilters();
    }
  },
  mounted() {
    this.planning_from_date = Vue.moment().format("YYYY-MM-DD");
    this.planning_to_date = Vue.moment()
      .add(12, "month")
      .format("YYYY-MM-DD");
  }
};
</script>

<style scoped lang="scss">
.week-row {
  display: grid;
  .day-cell {
    &:first-child {
      border-top-left-radius: 16px;
      border-bottom-left-radius: 16px;
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }
    &:last-child {
      border-top-right-radius: 16px;
      border-bottom-right-radius: 16px;
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }
    .strategies-container {
      width: 100%;
      .strategy-chip {
        border-radius: 8px;
        display: block;
        height: auto;
        line-height: 1;
        text-align: center;
        width: 100%;
        white-space: normal;
      }
    }
    &.clickable-day {
      cursor: pointer;
      &:hover {
        background-color: #f5fdff;
      }
    }
    &.empty-day {
      background-color: #fafafa;
      opacity: 0.5;
      cursor: default;
    }
  }
}
</style>
